import { Box, Stack, Theme, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import moment from 'moment';
import React, { useState } from 'react';
import Modal, { ModalRef } from 'src/components/Modal';
import { DATE_FORMAT_TYPE_V } from 'src/constants';
import { alertInstance } from 'src/contexts/AlertContext';
import { breakpointDown } from 'src/hooks/useBreakpoints';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { getFromToTimestamp } from 'src/hooks/useReportTable';
import { getReportMT, ReportMTResponse } from 'src/services/reportServices';
import { ButtonCustom } from 'src/styles/muiCustoms';
import { getDayOfWeekVI } from 'src/utils/moment';
import ReportTextInput from './TextInputReport';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '30%',
        height: '90%',
        backgroundColor: 'white',
        borderRadius: 4,
        padding: '2% 2%',
        boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
        ...breakpointDown(theme, {
            lg: {
                overflowY: 'auto',
                width: '35%',
                height: '90%',
                padding: '1% 1%',
            },
            md: {
                overflowY: 'auto',
                width: '70%',
                height: '60%',
                padding: '2% 2%',
            },
            xs: {
                width: '95%',
                height: '70%',
            },
        }),
    },
}));

interface ReportMTModalProps { }

export interface ReportMTModalRef {
    onOpen: (name: string, customerId: string, date: Date) => void;
}

type ReportMTData = {
    name: string;
    body: string;
    actor: string;
    total: number;
    oldMoney: number;
};

const ReportMTModal = React.forwardRef<ReportMTModalRef, ReportMTModalProps>(({ }, ref) => {
    const theme = useTheme();
    const classes = useStyles(theme);
    const [dataMessages, setDataMessages] = useState<ReportMTData>();
    const [value, copy] = useCopyToClipboard();
    const modalRef = React.useRef<ModalRef>();
    const [isLoading, setIsLoading] = useState(false);

    React.useImperativeHandle(
        ref,
        () => ({
            onOpen,
        }),
        [],
    );

    const onOpen = async (name: string, customerId: string, date: Date) => {
        modalRef.current.handleOpen();
        setDataMessages(undefined);
        try {
            const { from } = getFromToTimestamp({
                from: date,
            });
            setIsLoading(true);
            const response = await getReportMT({
                customerId,
                timestamp: from,
            });

            if (response?.data) {
                const [actor, total] = response.data.sum;
                setDataMessages({
                    name,
                    body: response.data.body,
                    actor: actor,
                    total: total,
                    oldMoney: 0,
                });
            }
        } catch (error) {
            alertInstance.handleOpen('error', 'Không thể tải báo sổ M.TÂY');
        } finally {
            setIsLoading(false);
        }
    };

    const textArea = React.useMemo<string>(() => {
        if (!dataMessages) {
            return '';
        }

        const { body, actor, total, oldMoney } = dataMessages;
        const totalWithOld = total + oldMoney;

        // Xác định "Lời" hay "Lỗ"
        const totalLabel = total >= 0 ? 'LỜI' : 'LỖ';
        const totalWithOldLabel = totalWithOld >= 0 ? 'LỜI' : 'LỖ';

        let result = body;
        result += `TỔNG ${actor} ${totalLabel}: ${total}\n`;

        if (oldMoney !== 0) {
            result += `TIỀN CŨ: ${oldMoney}\n`;
            result += `TC ${actor} ${totalWithOldLabel}: ${totalWithOld}`;
        }

        return result;
    }, [dataMessages]);

    const onClickCopy = (textArea: string) => {
        if (textArea) {
            copy(textArea);
            alertInstance.handleOpen('success', 'Sao chép thành công');
        }
    };

    const onUpdate = (value: number) => {
        setDataMessages((pre) => ({
            ...pre,
            oldMoney: value,
        }));
    };

    return (
        <Modal ref={modalRef}>
            <Box className={classes.container}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant={'h6'}>
                            {dataMessages ? `Báo sổ M.TÂY ${dataMessages.name}` : ''}
                        </Typography>
                    </Stack>
                    <Box mt={{ xl: '20px', lg: '15px', sm: '20px', xs: '15px' }}>
                        <ReportTextInput title="Nợ cũ:" onUpdate={onUpdate} />
                    </Box>
                    <textarea
                        value={!isLoading ? textArea : 'Loading...'}
                        aria-label="empty textarea"
                        style={{
                            width: '100%',
                            resize: 'none',
                            flex: '1',
                            fontSize: '1.25rem',
                            padding: '10px',
                            marginTop: '15px',
                            overflowY: 'auto',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontFamily: 'inherit',
                            lineHeight: '1.5',
                        }}
                        readOnly
                    />
                    <Box
                        mt={'15px'}
                        sx={{
                            display: 'flex',
                        }}
                    >
                        <ButtonCustom
                            sx={{
                                flex: 1,
                                color: 'white',
                                mr: '10px',
                            }}
                            myBackgroundColor={'#34BFA3'}
                            disabled={isLoading}
                            onClick={() => onClickCopy(textArea)}
                        >
                            Copy
                        </ButtonCustom>
                        <ButtonCustom
                            sx={{
                                color: 'white',
                            }}
                            myBackgroundColor={'#F4516C'}
                            onClick={() => {
                                modalRef.current.handleClose();
                            }}
                        >
                            Thoát
                        </ButtonCustom>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
});

export default ReportMTModal;
